import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { SectionLabel } from "@/components/ui-kit";
import { useLeadForm } from "@/hooks/useLeadForm";

const GOALS = [
  "Start investing / SIP",
  "Retirement planning",
  "Children's education",
  "Tax planning",
  "Insurance & protection",
  "Fixed income / bonds",
  "Loan enquiry",
  "Wealth creation",
  "Not sure yet",
];

const FIELD =
  "w-full rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3 text-[15px] text-white placeholder:text-white/40 outline-none transition-colors focus:border-gold focus:ring-2 focus:ring-gold/40";
const LABEL = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-white/60";

const Err = ({ msg }) => (msg ? <p className="mt-1 text-xs text-red-300">{msg}</p> : null);

const TextField = ({ id, label, testId, value, onChange, error, className = "", ...rest }) => (
  <div className={className}>
    <label className={LABEL} htmlFor={id}>{label}</label>
    <input id={id} data-testid={testId} className={FIELD} value={value} onChange={(e) => onChange(e.target.value)} {...rest} />
    <Err msg={error} />
  </div>
);

const Intro = () => (
  <div>
    <SectionLabel dark testId="lead-label">Personalised Plan</SectionLabel>
    <h2 className="mt-5 font-display text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl">
      Not sure where to start?
    </h2>
    <p className="mt-5 max-w-md text-base leading-relaxed text-white/70 sm:text-lg">
      Tell us about your financial goals, and we'll help you identify the right place to begin.
    </p>
    <ul className="mt-8 space-y-3">
      {["No pressure to invest immediately", "Guidance built around your goals", "A clear, sensible next step"].map((t) => (
        <li key={t} className="flex items-center gap-3 text-sm text-white/75">
          <CheckCircle2 className="h-5 w-5 shrink-0 text-gold" /> {t}
        </li>
      ))}
    </ul>
  </div>
);

const Success = () => (
  <motion.div
    key="success"
    data-testid="lead-success"
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    className="flex flex-col items-center py-10 text-center"
  >
    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gold/15">
      <CheckCircle2 className="h-9 w-9 text-gold" />
    </div>
    <h3 className="mt-6 font-display text-2xl font-bold text-white">Thank you!</h3>
    <p className="mt-3 max-w-sm text-white/70">We've received your details. Our team will get in touch with you shortly.</p>
  </motion.div>
);

const ExistingToggle = ({ value, onChange }) => (
  <div className="sm:col-span-2">
    <label className={LABEL}>Existing Investments?</label>
    <div className="flex gap-3">
      {["Yes", "No"].map((opt) => (
        <button
          key={opt}
          type="button"
          data-testid={`lead-existing-${opt.toLowerCase()}`}
          onClick={() => onChange(opt)}
          className={`flex-1 rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${
            value === opt ? "border-gold bg-gold text-navy" : "border-white/15 bg-white/[0.06] text-white/70 hover:border-white/40"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  </div>
);

const Consent = ({ checked, onChange, error }) => (
  <div className="sm:col-span-2">
    <label className="flex cursor-pointer items-start gap-3 text-sm text-white/70">
      <input type="checkbox" data-testid="lead-consent" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-gold" />
      <span>
        I agree to be contacted by Auxieron about my enquiry and accept the{" "}
        <a href="/privacy" className="font-semibold text-gold underline underline-offset-2">Privacy Policy</a>.
        My details will be used only to respond to this enquiry and are not shared publicly.
      </span>
    </label>
    <Err msg={error} />
  </div>
);

const LeadForm = () => {
  const { form, set, consent, setConsent, errors, status, submit } = useLeadForm(
    { name: "", mobile: "", email: "", age: "", monthly_income: "", monthly_investment: "", primary_goal: "", existing_investments: "" },
    (f) => ({ ...f, source: "instagram-landing" }),
  );

  if (status === "success") return <Success />;

  return (
    <motion.form key="form" data-testid="lead-form-el" onSubmit={submit} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <TextField id="lf-name" label="Name" testId="lead-name" value={form.name} onChange={(v) => set("name", v)} error={errors.name} placeholder="Your full name" className="sm:col-span-2" />
      <TextField id="lf-mobile" label="Mobile Number" testId="lead-mobile" value={form.mobile} onChange={(v) => set("mobile", v)} error={errors.mobile} placeholder="+91 ..." inputMode="tel" />
      <TextField id="lf-email" label="Email" testId="lead-email" value={form.email} onChange={(v) => set("email", v)} error={errors.email} placeholder="you@email.com" inputMode="email" />
      <TextField id="lf-age" label="Age" testId="lead-age" value={form.age} onChange={(v) => set("age", v)} placeholder="e.g. 30" inputMode="numeric" />
      <TextField id="lf-income" label="Monthly Income" testId="lead-income" value={form.monthly_income} onChange={(v) => set("monthly_income", v)} placeholder="e.g. ₹80,000" />
      <TextField id="lf-invest" label="Monthly Investment" testId="lead-investment" value={form.monthly_investment} onChange={(v) => set("monthly_investment", v)} placeholder="e.g. ₹15,000" />

      <div>
        <label className={LABEL} htmlFor="lf-goal">Primary Financial Goal</label>
        <select id="lf-goal" data-testid="lead-goal" className={`${FIELD} appearance-none`} value={form.primary_goal} onChange={(e) => set("primary_goal", e.target.value)}>
          <option value="" className="text-navy">Select a goal</option>
          {GOALS.map((g) => <option key={g} value={g} className="text-navy">{g}</option>)}
        </select>
      </div>

      <ExistingToggle value={form.existing_investments} onChange={(v) => set("existing_investments", v)} />
      <Consent checked={consent} onChange={setConsent} error={errors.consent} />

      {status === "error" && (
        <p data-testid="lead-error" className="sm:col-span-2 text-sm text-red-300">
          Something went wrong and your details weren't sent. Please try again, or reach us on WhatsApp.
        </p>
      )}

      <button
        type="submit"
        data-testid="lead-submit"
        disabled={status === "loading"}
        className="group sm:col-span-2 mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-4 text-[15px] font-bold text-navy shadow-gold transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#ffc41f] disabled:opacity-70"
      >
        {status === "loading" ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</>
        ) : (
          <>Get My Personalised Plan <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></>
        )}
      </button>
      <p className="sm:col-span-2 text-center text-[11px] text-white/40">
        No spam. Our team will reach out within 1–2 working days to help you plan your next step.
      </p>
    </motion.form>
  );
};

export const LeadMagnet = () => (
  <section id="lead-form" className="relative overflow-hidden bg-navy py-20 sm:py-28">
    <div className="grain absolute inset-0" />
    <div className="pointer-events-none absolute -right-24 top-10 h-80 w-80 rounded-full bg-royal/20 blur-3xl" />
    <div className="pointer-events-none absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-gold/10 blur-3xl" />

    <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-[0.9fr_1.1fr]">
      <Intro />
      <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm sm:p-8">
        <LeadForm />
      </div>
    </div>
  </section>
);
